import { skipToken, queryOptions } from '@tanstack/react-query';

import {
  RQ_SIMILAR_ITEMS,
  RQ_SIMILAR_TO_SOLD_ITEMS,
} from '../ReactQuery/cacheKeys';

import { type SimilarProduct } from './aliases';
import { fetchSimilarItems } from './api';
import { SIMILAR_TO_SOLD_RECS_PER_PRODUCT } from './constants';
import { type FetchSimilarItemsQueryParams } from './types';

export function similarItemsQueryOptions({
  productId,
  params,
  buyerCountry,
  withAuth,
  enabled,
}: {
  productId?: number;
  params: FetchSimilarItemsQueryParams;
  buyerCountry?: string;
  withAuth?: boolean;
  enabled?: boolean;
}) {
  const { country, lang, limit } = params;
  return queryOptions({
    queryKey: [RQ_SIMILAR_ITEMS, { productId, country, lang, limit }],
    enabled,
    queryFn: productId
      ? () =>
          fetchSimilarItems(
            String(productId),
            {
              ...params,
              /*
        This is a hack to get around cloudflare caching issues.
        "cb_country" is not a real query param on the webapi endpoint.
        The webapi endpoint uses caching which means users with different buyer country's will potentially see an incorrect cached price.
        Usually we would get around this in the web-api by using the Vary header, however, Cloudflare does not support Vary.
        There is a ticket to look into using cloudflare cache keys to get around this: https://depopmarket.atlassian.net/browse/WEB-6149
        Until we have a solution we are just passing the buyer country code as a query param so that we have unique caching per country code.
        Note: This wont work locally or on ephemerals for logged out users as we wont have the x-country-code injected from cloudflare into the website request.
        Use modheader to inject an x-country-code header.
        */
              // @ts-ignore
              cb_country: buyerCountry,
            },
            withAuth
          )
      : skipToken,
  });
}

export function similarToSoldQueryOptions({
  soldProductIds,
  params,
  buyerCountry,
  withAuth,
  enabled = true,
}: {
  soldProductIds: number[];
  params: FetchSimilarItemsQueryParams;
  buyerCountry?: string | null;
  withAuth?: boolean;
  enabled?: boolean;
}) {
  const { country, lang, limit } = params;

  return queryOptions({
    enabled,
    queryKey: [
      RQ_SIMILAR_TO_SOLD_ITEMS,
      { soldProductIds, country, lang, limit },
    ],
    queryFn:
      soldProductIds.length > 0
        ? async () => {
            const similarProductsMap = new Map<number, SimilarProduct>();
            const promises = soldProductIds.map((soldProductId) =>
              fetchSimilarItems(
                soldProductId.toString(),
                {
                  ...params,
                  // Fetches N recs for each product
                  limit: SIMILAR_TO_SOLD_RECS_PER_PRODUCT,
                  // @ts-expect-error - hack to get around cloudflare caching issues
                  cb_country: buyerCountry,
                },
                withAuth
              )
            );

            const responses = await Promise.allSettled(promises);

            responses.forEach((response) => {
              if (response.status === 'fulfilled') {
                const recsForProduct = response.value?.data?.objects || [];

                // Loop over the recs for each product
                for (const product of recsForProduct) {
                  // Find a rec that hasn't already been added to the list of similar products
                  // This prevents duplicate recs from showing in the module
                  if (!similarProductsMap.has(product.id)) {
                    similarProductsMap.set(product.id, product);
                    break;
                  }
                }
              }
            });

            return [...similarProductsMap.values()];
          }
        : skipToken,
  });
}
